import dayjs from 'dayjs'

import { getBrandName } from '@/src/utils/buildUtils'
import { BffAccountUserResponse } from '@/src/utils/profile/transformProfileResponse'
import { TravellerData, TravellerDocDetail, TravellerDocType } from '@/src/types/traveller'

const isTravellerDocType = (value: string): value is TravellerDocType =>
    value === 'PASSPORT' || value === 'NATIONALID' || value === 'IQAMA'

const toDoc = (doc: Partial<TravellerDocDetail>): TravellerDocDetail | null => {
    const docType = (doc.docType || '').toUpperCase()
    if (!isTravellerDocType(docType)) {
        return null
    }

    return {
        docType,
        docNumber: doc.docNumber || '',
        nationality: doc.nationality || '',
        countryIssued: doc.countryIssued || '',
        expiryDate: doc.expiryDate || '',
        dateOfBirth: doc.dateOfBirth || ''
    }
}

export function transformTravellersResponse(raw: BffAccountUserResponse): TravellerData[] {
    const isCleartrip = getBrandName() === 'cleartrip'
    const mainTravellerDetails = raw?.MAIN?.travellerDetails || []
    const linkedTravellerDetails = raw?.LINKED?.travellerDetails || []
    const allTravellerDetails = isCleartrip ? mainTravellerDetails : [...mainTravellerDetails, ...linkedTravellerDetails]
    const phoneSource = raw?.MAIN

    // Deduplicate travellers by API id after assembling the domain-specific source list.
    const seenIds = new Set<number>()
    const uniqueTravellers = allTravellerDetails.filter(traveller => {
        const id = traveller.id
        if (seenIds.has(id)) return false
        seenIds.add(id)
        return true
    })

    return uniqueTravellers.map(traveller => {
        const personalDetails = traveller?.personalDetails
        const contactInfo = traveller?.contactInfo
        const rawDateOfBirth = personalDetails?.dateOfBirth
        const dateOfBirth = rawDateOfBirth && dayjs(rawDateOfBirth).isValid() ? dayjs(rawDateOfBirth).format('YYYY-MM-DD') : ''
        const seenDocTypes = new Set<TravellerDocType>()
        const documents: TravellerDocDetail[] = []

        const rawDocs = traveller.docDetails || []
        rawDocs.forEach(doc => {
            const mappedDoc = toDoc(doc)
            if (!mappedDoc || seenDocTypes.has(mappedDoc.docType)) {
                return
            }

            seenDocTypes.add(mappedDoc.docType)
            documents.push(mappedDoc)
        })

        return {
            id: traveller.id,
            isRegistered: Boolean(traveller?.isRegistered),
            title: personalDetails?.title || '',
            firstName: personalDetails?.firstName || '',
            lastName: personalDetails?.lastName || '',
            gender: personalDetails?.gender || '',
            dateOfBirth,
            email: contactInfo?.emails?.[0]?.emailId || '',
            phone: {
                countryCode: (phoneSource?.countryCode || '').replace(/^\+/, ''),
                number: phoneSource?.mobile || ''
            },
            documents
        }
    })
}
